/**
 * #959: 録画を切った作品（`pyxel.init(capture_sec=0)` など）の表紙を、静止画で代用する。
 *
 * Pyxel は録画が 0 フレームだと GIF を作らないが、`pyxel.screenshot()`（PNG）は撮れる。
 * 表紙は GIF 限定（presigned POST の magic bytes 検査・`/d/{hash}.gif`・`image/gif` 固定。
 * `lib/src/gennai/services/cover_upload.py`）なので、ペアで撮っている OGP 用の PNG（#707）を
 * 1 フレームの GIF に変換して表紙にする。
 *
 * Pyxel のパレットは最大 256 色（`MAX_COLORS`）なので、色表を自前で作れば**可逆**に変換できる。
 * 256 色を超えるのは想定外の入力だけで、そのときに限り gifenc の量子化に落とす。
 */
import { GIFEncoder, applyPalette, quantize } from 'gifenc'
import type { Palette } from 'gifenc'

const MAX_GIF_COLORS = 256

/** RGBA 画素を 1 フレームの GIF にする。256 色以内なら可逆。 */
export function encodeIndexedGif(rgba: Uint8Array | Uint8ClampedArray, width: number, height: number): Uint8Array {
  const pixelCount = width * height
  const index = new Uint8Array(pixelCount)
  const table = new Map<number, number>()
  const palette: Palette = []
  let overflow = false
  for (let i = 0; i < pixelCount; i++) {
    const o = i * 4
    const rgb = (rgba[o]! << 16) | (rgba[o + 1]! << 8) | rgba[o + 2]!
    let idx = table.get(rgb)
    if (idx === undefined) {
      if (palette.length >= MAX_GIF_COLORS) {
        overflow = true
        break
      }
      idx = palette.length
      table.set(rgb, idx)
      palette.push([rgba[o]!, rgba[o + 1]!, rgba[o + 2]!])
    }
    index[i] = idx
  }

  const gif = GIFEncoder()
  if (overflow) {
    const quantized = quantize(rgba, MAX_GIF_COLORS)
    gif.writeFrame(applyPalette(rgba, quantized), width, height, { palette: quantized })
  }
  else {
    gif.writeFrame(index, width, height, { palette })
  }
  gif.finish()
  return gif.bytes()
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('still image failed to decode'))
    img.src = src
  })
}

/**
 * PNG の data URL（`pyxel.screenshot(scale=N)`）を、等倍に戻した GIF の data URL にする。
 * 通常の表紙は `pyxel.screencast(scale=1)` なので、大きさをそろえる。N×N のブロックは
 * すべて同じ色なので、smoothing を切った縮小は 1 画素も変わらない。
 */
export async function stillToGifDataUrl(pngDataUrl: string, stillScale: number): Promise<string> {
  const img = await loadImage(pngDataUrl)
  const scale = Math.max(1, Math.floor(stillScale))
  const width = Math.max(1, Math.round(img.naturalWidth / scale))
  const height = Math.max(1, Math.round(img.naturalHeight / scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2d canvas is not available')
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(img, 0, 0, width, height)
  const { data } = ctx.getImageData(0, 0, width, height)
  return `data:image/gif;base64,${bytesToBase64(encodeIndexedGif(data, width, height))}`
}
